// copied from https://github.com/ForgeRock/forgerock-javascript-sdk codebase
// modified to fit CMC Markets auth flow needs.
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { authenticatedJourneyTree, ERROR_CODE_SESSION_TIMEOUT, FRErrorMessage } from '../constants/FRConstants';
import { ErrorMessageCode, JourneyHandlerActionType, JourneyTree } from '../constants/FREnums';
import { IErrorOutcome, parse } from '../utils/FRErrorParser';
import { CMCFRSDK } from '../utils/FRConfigSetup';
import { ASB_RESUME_ERROR } from '../utils';

export type FRFormStep = CMCFRSDK.FRStep & { retry?: boolean; stepTimeout?: boolean; errorOutcome?: IErrorOutcome };
export type FRLoginFailureEnhanced = CMCFRSDK.FRLoginFailure & { errorOutcome?: IErrorOutcome };
export type FROutcomeStep = (CMCFRSDK.FRLoginSuccess | FRLoginFailureEnhanced) & { retry?: boolean; stepTimeout?: boolean };
export type Step = FRFormStep | FROutcomeStep;

export type JourneyHandlerAction = {
  type: JourneyHandlerActionType;
  tree: string;
  forceCmcSignout?: (falconDisabled?: boolean) => void;
  falconDisabled?: boolean;
  authResume?: boolean;
};

export type JourneyHandlerResponse = {
  renderStep: Step;
  user: any;
  setSubmissionStep: Dispatch<SetStateAction<Step | undefined>>;
};

/** *********************************************************************
 * SDK INTEGRATION POINT
 * Summary: Get OAuth/OIDC tokens with Authorization Code Flow w/PKCE.
 * ----------------------------------------------------------------------
 * Details: Since we have successfully authenticated the user, we can now
 * get the OAuth2/OIDC tokens. We are passing the `forceRenew` option to
 * ensure we get fresh tokens, regardless of existing tokens.
 ************************************************************************* */
/** *********************************************************************
 * SDK INTEGRATION POINT
 * Summary: Call the user info endpoint for some basic user data.
 * ----------------------------------------------------------------------
 * Details: This is an OAuth2 call that returns user information with a
 * valid access token. This is optional and only used for displaying
 * user info in the UI.
 ********************************************************************* */
export const getOAuth = async () => {
  try {
    await CMCFRSDK.TokenManager.getTokens({ forceRenew: true, login: `embedded` });
  } catch (error) {
    console.info(`error: get tokens: ${error}`);
  }

  let currentUser;
  try {
    currentUser = await CMCFRSDK.UserManager.getCurrentUser();
  } catch (error) {
    console.error(`error: get current user; ${error}`);
    currentUser = {};
  }
  return currentUser;
};

/**
 * Construct base failure/error step.
 * For unhandled errors, builds a generic error step structure.
 */
const getBaseErrorStep = (message: string): Step => {
  return {
    type: CMCFRSDK.StepType.LoginFailure,
    payload: { message }
  } as Step;
};

/**
 * Theis is called only when login flow fails.
 * The type of the step is FRLoginFailure.
 */
export const getErrorStep = async (nextStep: CMCFRSDK.FRLoginFailure, tree: string): Promise<Step> => {
  let newStep: Step;
  const errorMessage = parse(nextStep.payload?.message);

  // these are parsed special errors, the ones that contain a specific code in messages.
  // e.g. message: `code=ACCOUNT_INACTIVE,customerRef=2377-1424`.
  // should be handled by the ui.
  if (nextStep.payload?.message === ASB_RESUME_ERROR) {
    newStep = nextStep;
  } else if (errorMessage.code && FRErrorMessage[errorMessage.code as ErrorMessageCode]) {
    newStep = {
      type: CMCFRSDK.StepType.LoginFailure,
      errorOutcome: errorMessage
    } as Step;
  } else {
    try {
      // if not interested in response error (e.g. not part of the special errors)
      // try loading next step by passing first step as undefined.
      // this will automatically reload login step.
      newStep = await CMCFRSDK.FRAuth.next(undefined, { tree });
      if (nextStep.payload?.code === 401) {
        (newStep as FRFormStep).errorOutcome = { code: `401` };
      }
    } catch (error) {
      // in case of failure return a generic error structure
      console.error(`error: failure in new step request: ${error}`);
      newStep = getBaseErrorStep(`unknown request failure`);
    }
  }
  return newStep;
};

/** *********************************************************************
 * SDK INTEGRATION POINT
 * Summary: Call the SDK's next method to submit the current step.
 * ----------------------------------------------------------------------
 * Details: This calls the next method with the previous step, expecting
 * the next step to be returned, or a success or failure.
 ********************************************************************* */
export const getNextStep = async (prev: Step | undefined, tree: string) => {
  let nextStep: Step;
  try {
    nextStep = await CMCFRSDK.FRAuth.next(prev as CMCFRSDK.FRStep, { tree });
  } catch (error) {
    console.error(`error: failure in next step request: ${error}`);
    nextStep = getBaseErrorStep(`unknown request failure`);
  }
  return nextStep;
};

export const resumeAuthJourney = async () => {
  let nextStep: Step;
  const maxRetries = 3;

  const attemptResume = async (attempt = 1): Promise<Step> => {
    try {
      // eslint-disable-next-line unicorn/prefer-global-this
      return await CMCFRSDK.FRAuth.resume(window.location.href);
    } catch (error) {
      if (attempt < maxRetries) {
        // Calculate delay with exponential backoff (1s, 2s, 4s)
        const delayMs = Math.pow(2, attempt - 1) * 1000;
        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return attemptResume(attempt + 1);
      }

      console.error(`Failure in resumeAuthJourney: ${error}`);
      return getBaseErrorStep(ASB_RESUME_ERROR);
    }
  };

  nextStep = await attemptResume();
  return nextStep;
};

export const useJourneyHandler = ({ type, tree, forceCmcSignout, falconDisabled, authResume }: JourneyHandlerAction) => {
  const [renderStep, setRenderStep] = useState<Step>();
  const [submissionStep, setSubmissionStep] = useState<Step>();
  const [submissionProcess, setSubmissionProcess] = useState(false);
  const [isInitialState, setIsInitialState] = useState(true);
  const [user, setUser] = useState<any>();

  useEffect(() => {
    const setAuth = async () => {
      const authUser = await getOAuth();
      setUser(authUser);
    };

    const getStep = async (prev: Step | undefined) => {
      const previousStage = prev?.payload?.stage;
      const previousPayload = prev?.payload;

      const nextStep = authResume ? await resumeAuthJourney() : await getNextStep(prev, tree);

      if (nextStep.type === CMCFRSDK.StepType.LoginSuccess) {
        if (!authenticatedJourneyTree[tree as JourneyTree] && !falconDisabled) {
          await setAuth();
        }
        setRenderStep(nextStep);
      } else if (nextStep.type === CMCFRSDK.StepType.LoginFailure) {
        if (!!authenticatedJourneyTree[tree as JourneyTree] && isInitialState && !!forceCmcSignout) {
          setIsInitialState(false);
          forceCmcSignout!(falconDisabled);
          return;
        }

        const newStep = await getErrorStep(nextStep, tree);

        if (previousStage && newStep.payload?.stage === previousStage) {
          (newStep as CMCFRSDK.FRStep).callbacks = (prev as CMCFRSDK.FRStep)?.callbacks;
          newStep.payload = {
            ...previousPayload,
            authId: newStep.payload.authId
          };
          prev!.retry = true;

          // casting this to any cause the current SDK StepDetail field doesn't have errorCode prop.
          // however when session timing out the server responds with errorCode.
          if ((nextStep.payload?.detail as any)?.errorCode === ERROR_CODE_SESSION_TIMEOUT) {
            prev!.stepTimeout = true;
          }
        }

        setRenderStep(newStep);
        setSubmissionProcess(false);
      } else {
        setRenderStep(nextStep);
        setSubmissionProcess(false);
      }
      setIsInitialState(false);
    };
    getStep(submissionStep);
  }, [type, tree, submissionStep]);

  return {
    renderStep,
    user,
    submissionStep,
    setSubmissionStep,
    submissionProcess,
    setSubmissionProcess
  };
};
